import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { MAX_UPLOAD_BYTES, TICKET_CATEGORIES,
  ticketCategoryLabel, type Ticket, type TicketCategory, type TicketDetail } from '@platform/shared';
import { cx, Modal, Spinner } from '../../components/ui';
import { api, downloadFile } from '../../lib/api';
import { useSearch } from '../../lib/router';
import { useLoad, useVersion } from '../../stores/account';
import { useMe } from '../../stores/session';
import { AIcon, Card, Chip, errMsg, Field, H1, H2, List, Notice, PillButton, when } from './kit';

const STATUS: Record<Ticket['status'], [string, 'warn' | 'accent' | 'muted']> = {
  open: ['Open', 'warn'],
  awaiting_user: ['Reply needed', 'accent'],
  resolved: ['Resolved', 'muted'],
};
const catLabel = ticketCategoryLabel;

/** Subject picker styled like the reference: blue border and label when open, bold options. */
function SubjectSelect({ value, onChange }: { value: TicketCategory | null; onChange: (c: TicketCategory) => void }) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const wrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => !wrap.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const choose = (c: TicketCategory) => {
    onChange(c);
    setOpen(false);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      else setHi((h) => Math.min(h + 1, TICKET_CATEGORIES.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHi((h) => Math.max(h - 1, 0));
    } else if ((e.key === 'Enter' || e.key === ' ') && open) {
      e.preventDefault();
      choose(TICKET_CATEGORIES[hi]!.id);
    } else if (e.key === 'Escape') setOpen(false);
  };
  return (
    <div ref={wrap} className="relative flex flex-col gap-1.5">
      <span id="subj-label" className={cx('text-[14px] font-medium', open ? 'text-accent' : 'text-muted')}>
        Subject
      </span>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby="subj-label subj-value"
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onKey}
        className={cx('flex h-[56px] items-center gap-3 rounded-[10px] border bg-card px-4 text-left text-[16px]', open ? 'border-accent' : 'border-line-strong')}
      >
        <span id="subj-value" className={cx('flex-1 truncate', value ? 'text-fg' : 'text-muted')}>
          {value ? catLabel(value) : 'Enter topic'}
        </span>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true" className={cx('text-muted transition-transform', open && 'rotate-180')}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <ul role="listbox" aria-labelledby="subj-label" className="shadow-pop absolute top-[calc(100%+6px)] right-0 left-0 z-30 m-0 max-h-[260px] list-none overflow-y-auto rounded-[10px] border border-line bg-panel p-0 py-1.5">
          {TICKET_CATEGORIES.map((c, i) => (
            <li
              key={c.id}
              role="option"
              aria-selected={value === c.id}
              onMouseEnter={() => setHi(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(c.id)}
              className={cx('cursor-pointer px-4 py-2.5 text-[16px] font-semibold text-fg', i === hi && 'bg-raised')}
            >
              {c.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Attachments({ files, setFiles }: { files: File[]; setFiles: (f: File[]) => void }) {
  const [err, setErr] = useState<string | null>(null);
  return (
    <div className="flex flex-col gap-2">
      <label className="flex cursor-pointer items-center gap-4 rounded-[20px] border border-dashed border-line-strong p-4 text-[15px] text-muted hover:text-fg md:p-5 md:text-[16px]">
        <span className="grid size-11 shrink-0 place-items-center rounded-full bg-track">
          <AIcon name="clip" />
        </span>
        <span className="min-w-0 flex-1 truncate">{files.length ? files.map((f) => f.name).join(', ') : 'Attach screenshots · up to 3 files (JPG, PNG, PDF), 10 MB each'}</span>
        <input
          type="file"
          multiple
          accept="image/jpeg,image/png,application/pdf"
          className="hidden"
          onChange={(e) => {
            const list = [...(e.target.files ?? [])];
            if (list.length > 3) return setErr('Attach up to 3 files');
            if (list.some((f) => f.size > MAX_UPLOAD_BYTES)) return setErr('Each file must be 10 MB or smaller');
            setErr(null);
            setFiles(list);
          }}
        />
      </label>
      {err && <span className="text-[13px] text-down">{err}</span>}
    </div>
  );
}

function NewTicket({ onCreated }: { onCreated: (t: TicketDetail) => void }) {
  const me = useMe()!;
  const search = useSearch();
  const [category, setCategory] = useState<TicketCategory | null>(() => {
    const t = new URLSearchParams(search).get('topic');
    return TICKET_CATEGORIES.some((c) => c.id === t) ? (t as TicketCategory) : null;
  });
  // Opened from a deposit ticket: ?ref=<payment ID> fills the reference.
  const [reference, setReference] = useState(() => (new URLSearchParams(search).get('ref') ?? '').slice(0, 100));
  const [phone, setPhone] = useState('');
  const [windowSel, setWindowSel] = useState('09:00 – 12:00');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  // The ticket just sent, shown in the confirmation pop-up until it is closed.
  const [sent, setSent] = useState<TicketDetail | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!category) return setMsg({ ok: false, text: 'Choose a topic' });
    setBusy(true);
    setMsg(null);
    try {
      const fd = new FormData();
      fd.append('category', category);
      fd.append('message', message);
      if (reference) fd.append('reference', reference);
      if (category === 'callback') {
        fd.append('phone', phone);
        fd.append('callbackWindow', windowSel);
      }
      for (const f of files) fd.append('file', f, f.name);
      const t = await api.upload<TicketDetail>('/api/support/tickets', fd);
      setMessage('');
      setReference('');
      setFiles([]);
      setSent(t);
      onCreated(t);
    } catch (x) {
      setMsg({ ok: false, text: errMsg(x) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card>
      <form onSubmit={submit} className="flex flex-col gap-7">
        <div>
          <H1>Contact support</H1>
          <p className="m-0 mt-2 text-[16px] text-muted md:text-[17px]">We reply by email and here, usually within 24 hours on business days.</p>
        </div>
        <SubjectSelect value={category} onChange={setCategory} />
        {category === 'funding' && (
          <Field label={<>Transaction hash or request ID <span className="font-normal text-muted">(optional)</span></>} htmlFor="st-ref">
            <input id="st-ref" className="field num" placeholder="0x… or WD-…" value={reference} onChange={(e) => setReference(e.target.value)} />
          </Field>
        )}
        {category === 'callback' && (
          <div className="grid gap-6 md:grid-cols-2">
            <Field label="Phone" htmlFor="st-phone">
              <input id="st-phone" required inputMode="tel" autoComplete="tel" className="field num" placeholder="+44 20 7946 0958" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </Field>
            <Field label="Best time to call" htmlFor="st-window" hint="In your timezone">
              <select id="st-window" className="field" value={windowSel} onChange={(e) => setWindowSel(e.target.value)}>
                {['09:00 – 12:00', '12:00 – 15:00', '15:00 – 18:00', '18:00 – 21:00'].map((w) => (
                  <option key={w}>{w}</option>
                ))}
              </select>
            </Field>
          </div>
        )}
        <Field label="Message" htmlFor="st-msg">
          <textarea id="st-msg" required rows={6} maxLength={5000} className="field" placeholder="Describe the issue. Include amounts, times and IDs where you can." value={message} onChange={(e) => setMessage(e.target.value)} />
        </Field>
        <Attachments files={files} setFiles={setFiles} />
        <Notice msg={msg} onClose={() => setMsg(null)} />
        <PillButton type="submit" size="lg" busy={busy} disabled={!category || !message.trim()} className="w-full">
          Submit ticket
        </PillButton>
      </form>
      <Modal open={!!sent} onClose={() => setSent(null)} title="Ticket sent">
        {sent && (
          <>
            <span className="mx-auto grid size-14 place-items-center rounded-full bg-up-bg text-up">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12.5l4.5 4.5L19 7.5" />
              </svg>
            </span>
            <p className="m-0 text-center text-[20px] font-bold">We've got your request</p>
            <p className="m-0 text-center text-[14px] text-muted">
              Ticket <b className="num text-fg">{sent.number}</b> is open. We reply by email to <b className="text-fg">{me.email}</b> and here under My tickets, usually within 24 hours on business days.
            </p>
            <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 rounded-xl border border-line px-3.5 py-3 text-[13px]">
              <dt className="text-muted">Ticket</dt>
              <dd className="num m-0 text-right font-medium">{sent.number}</dd>
              <dt className="text-muted">Topic</dt>
              <dd className="m-0 text-right font-medium break-words">{catLabel(sent.category)}</dd>
              <dt className="text-muted">Status</dt>
              <dd className="m-0 text-right font-medium">{STATUS[sent.status][0]}</dd>
            </dl>
            <PillButton onClick={() => setSent(null)} className="w-full">
              Done
            </PillButton>
          </>
        )}
      </Modal>
    </Card>
  );
}

function Thread({ id, onChanged }: { id: string; onChanged: () => void }) {
  const v = useVersion('ticket');
  const { data: t, setData, error } = useLoad<TicketDetail>(`/api/support/tickets/${id}`, [v]);
  const [reply, setReply] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => end.current?.scrollIntoView({ block: 'nearest' }), [t?.messages.length]);
  if (error) return <Notice msg={{ ok: false, text: error }} />;
  if (!t) return <Spinner />;
  const send = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const fd = new FormData();
      fd.append('message', reply);
      for (const f of files) fd.append('file', f, f.name);
      setData(await api.upload<TicketDetail>(`/api/support/tickets/${id}/messages`, fd));
      setReply('');
      setFiles([]);
      onChanged();
    } catch (x) {
      setErr(errMsg(x));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="m-0 text-[18px] font-semibold md:text-[20px]">
          {t.number} · {t.subject}
        </h2>
        <Chip tone={STATUS[t.status][1]}>{STATUS[t.status][0]}</Chip>
      </div>
      <div className="text-[14px] text-muted">
        {catLabel(t.category)}
        {t.reference ? ` · ${t.reference}` : ''}
        {t.phone ? ` · Call ${t.phone}${t.callbackWindow ? `, ${t.callbackWindow}` : ''}` : ''}
      </div>
      <div className="flex max-h-[460px] flex-col gap-3 overflow-y-auto text-[15px] md:text-[16px]">
        {t.messages.map((m) => (
          <div key={m.id} className={cx('max-w-[85%] rounded-[18px] p-4 whitespace-pre-wrap', m.author === 'user' ? 'self-end rounded-br-md bg-track' : 'rounded-bl-md border border-line')}>
            {m.author === 'staff' && <b className="block text-[14px]">{m.authorName}</b>}
            {m.body}
            {m.attachments.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {m.attachments.map((a) => (
                  <button key={a.id} type="button" onClick={() => void downloadFile(`/api/support/attachments/${a.id}`, a.filename).catch(() => setErr('Could not download the file'))} className="inline-flex items-center gap-1.5 rounded-full bg-pill px-3 py-1 text-[13px] font-semibold">
                    <AIcon name="clip" size={13} />
                    {a.filename}
                  </button>
                ))}
              </div>
            )}
            <div className="num mt-1.5 text-[12px] text-muted">{when(m.createdAt)}</div>
          </div>
        ))}
        <div ref={end} />
      </div>
      <form onSubmit={send} className="flex flex-col gap-3">
        <textarea aria-label="Reply" rows={3} maxLength={5000} className="field" placeholder={t.status === 'resolved' ? 'Reply to reopen this ticket' : 'Write a reply'} value={reply} onChange={(e) => setReply(e.target.value)} />
        <Attachments files={files} setFiles={setFiles} />
        {err && <p className="m-0 text-[14px] text-down" role="alert">{err}</p>}
        <PillButton type="submit" busy={busy} disabled={!reply.trim()} className="self-end">
          Send
        </PillButton>
      </form>
    </Card>
  );
}

export function SupportPage() {
  const v = useVersion('ticket');
  const { data: tickets, reload } = useLoad<Ticket[]>('/api/support/tickets', [v]);
  const [selected, setSelected] = useState<string | null>(null);
  useEffect(() => {
    if (!selected && tickets?.length) setSelected((tickets.find((t) => t.status === 'awaiting_user') ?? tickets[0]!).id);
  }, [tickets, selected]);
  return (
    <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-8 xl:grid-cols-[minmax(0,1fr)_minmax(0,500px)] 2xl:grid-cols-[minmax(0,1fr)_minmax(0,564px)]">
      <NewTicket
        onCreated={(t) => {
          void reload();
          setSelected(t.id);
        }}
      />
      <div className="flex min-w-0 flex-col gap-8">
        <Card>
          <div className="flex items-center justify-between">
            <H2>My tickets</H2>
            {tickets && <span className="text-[14px] text-muted">{tickets.length} total</span>}
          </div>
          <div className="mt-6">
            <List empty="No tickets yet">
              {(tickets ?? []).map((t) => (
                <button key={t.id} type="button" onClick={() => setSelected(t.id)} className={cx('block w-full rounded-xl text-left', selected === t.id && 'bg-track px-3')}>
                  <div className="flex items-center gap-4 border-b border-line py-4">
                    <span className="grid size-11 shrink-0 place-items-center rounded-full bg-track">
                      <AIcon name="help" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[16px] md:text-[17px]">{t.subject}</div>
                      <div className="truncate text-[14px] text-muted">
                        <span className="num">{t.number}</span> · {catLabel(t.category)} · {when(t.updatedAt)}
                      </div>
                    </div>
                    <Chip tone={STATUS[t.status][1]}>{STATUS[t.status][0]}</Chip>
                  </div>
                </button>
              ))}
            </List>
          </div>
        </Card>
        {selected && <Thread key={selected} id={selected} onChanged={() => void reload()} />}
      </div>
    </div>
  );
}
